import React from "react";
import { Box, Chip, Divider, Grid2, Paper, useTheme } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import { formatDateWithoutTime } from "../../../../../../views/components/common/functions/formatDate";
import {
  DailyTasksDetailResult,
  DailyTasksSlaStatus,
  FILE_WORK_STATE_LABELS,
  FileWorkState,
} from "../../../../../../models/Dashboard/DailyTasks/DailyTasks";
import {
  getModernPageColors,
  modernSectionPaperSx,
} from "../../../../../../styles/modernListingPageSx";
import { dailyTasksChipSx, getDailyTasksChipColors } from "./dailyTasksChips";
import { filenameFromUrl, ImagePreviewFrame } from "./DailyTasksMedia";
import { useDailyTasksFieldPermissions } from "./useDailyTasksFieldPermissions";
import {
  DailyTasksEmptyState,
  DailyTasksSectionHeader,
} from "./DailyTasksEmptyState";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";

interface FileDrawerBodyProps {
  detail: DailyTasksDetailResult;
}

const formatDate = (value: string | Date | null | undefined): string => {
  if (!value) return "-";
  return formatDateWithoutTime(value) || "-";
};

const formatHours = (hours: number | null | undefined): string => {
  if (hours == null || !Number.isFinite(Number(hours))) return "-";
  const value = Math.round(Number(hours));
  if (value < 24) return `${value}h`;
  const days = Math.floor(value / 24);
  const rem = value % 24;
  return rem ? `${days}d ${rem}h` : `${days}d`;
};

const formatCost = (value: number | null | undefined): string => {
  if (value == null || !Number.isFinite(Number(value))) return "-";
  return String(Number(value));
};

const fileWorkLabel = (
  state: DailyTasksDetailResult["mockup_work_state"],
): string => {
  if (state && state in FILE_WORK_STATE_LABELS) {
    return FILE_WORK_STATE_LABELS[state as FileWorkState];
  }
  return state ? state.replace(/_/g, " ") : "Complete";
};

const slaTone = (
  theme: Theme,
  status: DailyTasksSlaStatus,
): { bg: string; fg: string; label: string } => {
  try {
    if (status === "bad") {
      return { ...getDailyTasksChipColors(theme, "bad"), label: "Overdue" };
    }
    if (status === "warn") {
      return { ...getDailyTasksChipColors(theme, "warn"), label: "Watch" };
    }
    if (status === "ok") {
      return { ...getDailyTasksChipColors(theme, "ok"), label: "On track" };
    }
    return { ...getDailyTasksChipColors(theme, "info"), label: "Pending" };
  } catch (error) {
    return { ...getDailyTasksChipColors(theme, "info"), label: "Pending" };
  }
};

const StatTile: React.FC<{
  eyebrow: string;
  value: string;
  hint?: string;
  pill?: { label: string; bg: string; fg: string };
}> = ({ eyebrow, value, hint, pill }) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  return (
    <Box sx={{ height: "100%" }}>
      <CustomTypography
        variant="caption"
        fontWeight={600}
        sx={{
          color: colors.greyTextColor,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        }}
      >
        {eyebrow}
      </CustomTypography>
      <CustomTypography
        variant="heading5"
        fontWeight={600}
        sx={{
          color: colors.navyText,
          letterSpacing: "-0.02em",
          lineHeight: 1.1,
          mt: 0.5,
        }}
      >
        {value}
      </CustomTypography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 0.75,
          mt: 0.75,
        }}
      >
        {pill ? (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              px: 1,
              py: 0.25,
              borderRadius: 1,
              bgcolor: pill.bg,
              color: pill.fg,
            }}
          >
            <CustomTypography
              variant="caption"
              fontWeight={600}
              sx={{ color: pill.fg }}
            >
              {pill.label}
            </CustomTypography>
          </Box>
        ) : null}
        {hint ? (
          <CustomTypography variant="caption" sx={{ color: colors.greyTextColor }}>
            {hint}
          </CustomTypography>
        ) : null}
      </Box>
    </Box>
  );
};

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = useTheme();
  return (
    <CustomTypography
      variant="caption"
      fontWeight={600}
      sx={{
        color: getModernPageColors(theme).subtitle,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        display: "block",
        mb: 1.25,
      }}
    >
      {children}
    </CustomTypography>
  );
};

const MetaField: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => {
  const theme = useTheme();
  const pageColors = getModernPageColors(theme);
  return (
    <Grid2 size={{ xs: 6, sm: 4 }}>
      <CustomTypography
        variant="caption"
        fontWeight={600}
        sx={{
          color: pageColors.subtitle,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </CustomTypography>
      <CustomTypography
        variant="body2"
        fontWeight={500}
        sx={{ color: pageColors.title, mt: 0.25 }}
      >
        {value}
      </CustomTypography>
    </Grid2>
  );
};

const FileDrawerBody: React.FC<FileDrawerBodyProps> = ({ detail }) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const { canViewDispatchDate, canViewCostPrice } = useDailyTasksFieldPermissions();
  const sla = slaTone(theme, detail.sla_status);

  const daysHint =
    detail.days_to_sla == null
      ? undefined
      : detail.days_to_sla < 0
        ? `${Math.abs(detail.days_to_sla)}d overdue`
        : detail.days_to_sla === 0
          ? "Due today"
          : `${detail.days_to_sla}d left`;

  const clockHint =
    detail.mockup_work_state === "needs_send"
      ? "Since last printing file"
      : "Since mockup approval";

  return (
    <>
      <Paper elevation={0} sx={modernSectionPaperSx(theme)}>
        <Grid2 container spacing={2} columns={12}>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <StatTile
              eyebrow="Elapsed"
              value={formatHours(detail.sla_elapsed_hours)}
              hint={`Since ${formatDate(detail.sla_clock_start)}`}
              pill={{ label: sla.label, bg: sla.bg, fg: sla.fg }}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <StatTile
              eyebrow={
                detail.mockup_work_state === "needs_send"
                  ? "Send SLA"
                  : "File SLA"
              }
              value={formatHours(detail.sla_target_hours)}
              hint={daysHint ?? clockHint}
            />
          </Grid2>
        </Grid2>
        <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
          <Chip
            size="small"
            label={fileWorkLabel(detail.mockup_work_state)}
            sx={dailyTasksChipSx(
              theme,
              detail.mockup_work_state === "needs_file"
                ? "bad"
                : detail.mockup_work_state === "needs_send"
                  ? "info"
                  : "ok",
            )}
          />
          <CustomTypography
            variant="caption"
            sx={{ color: colors.greyTextColor, alignSelf: "center" }}
          >
            {clockHint}
          </CustomTypography>
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ ...modernSectionPaperSx(theme), mt: 2 }}>
        <SectionLabel>Order</SectionLabel>
        <Grid2 container spacing={1.5} columns={12}>
          <MetaField label="Type" value={detail.order_type || "-"} />
          <MetaField label="Qty" value={String(detail.ordered_quantity)} />
          <MetaField label="Printer" value={detail.printer_name || "-"} />
          <MetaField label="Print type" value={detail.printing_type || "-"} />
          {canViewCostPrice ? (
            <MetaField
              label="Print cost"
              value={formatCost(detail.printing_cost)}
            />
          ) : null}
          <MetaField
            label="Approved"
            value={formatDate(detail.mockups_approved_at)}
          />
          <MetaField label="PM" value={detail.pm_poc || "Unassigned"} />
          <MetaField label="Sales" value={detail.sales_poc || "-"} />
          {canViewDispatchDate && detail.dispatch_date ? (
            <MetaField label="Dispatch" value={formatDate(detail.dispatch_date)} />
          ) : null}
        </Grid2>
        <Divider sx={{ my: 2 }} />
        <SectionLabel>File clock</SectionLabel>
        <Grid2 container spacing={1.5} columns={12}>
          <MetaField
            label="File TAT"
            value={formatHours(detail.file_upload_tat_hours)}
          />
          <MetaField
            label="Send TAT"
            value={formatHours(detail.cdr_to_printer_tat_hours)}
          />
          <MetaField
            label="Total TAT"
            value={formatHours(detail.total_tat_hours)}
          />
        </Grid2>
      </Paper>

      <Box sx={{ mt: 3, mb: 2 }}>
        <DailyTasksSectionHeader
          title="Printing files"
          count={detail.printing_files.length}
        />
        {detail.printing_files.length === 0 ? (
          <DailyTasksEmptyState
            icon={ImageOutlinedIcon}
            title="No printing files yet"
            description="CDR / printing files uploaded for this product will appear here."
            sx={{ mt: 0 }}
          />
        ) : (
          <Grid2 container spacing={1.5} columns={12}>
            {detail.printing_files.map((file) => (
              <Grid2 key={file.id} size={{ xs: 12, sm: 6 }}>
                <Box
                  sx={{
                    border: `1px solid ${colors.softBorder}`,
                    borderRadius: 2,
                    overflow: "hidden",
                    bgcolor: colors.white,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <ImagePreviewFrame
                    src={file.image_url}
                    alt="printing file"
                    filename={filenameFromUrl(
                      file.image_url,
                      `printing-file-${file.id}.jpg`,
                    )}
                  />
                  <Box sx={{ p: 1.25 }}>
                    <CustomTypography
                      variant="body3"
                      fontWeight={500}
                      sx={{ display: "block" }}
                    >
                      {file.admin_name || "Unknown"} ·{" "}
                      {formatDate(file.created_date)}
                    </CustomTypography>
                    <CustomTypography variant="body3" fontWeight={500}>
                      {Boolean(file.sent_to_printer)
                        ? `Sent ${formatDate(file.sent_to_printer_date)}`
                        : "Not sent to printer"}
                    </CustomTypography>
                  </Box>
                </Box>
              </Grid2>
            ))}
          </Grid2>
        )}
      </Box>
    </>
  );
};

export default FileDrawerBody;
